// MMO 공유 필드 클라(REBIRTH-MODES-SPEC 4.2, 레인 M). 게임 쪽 — 렌더와 분리.
// 레인 F 필드 장면이 매 틱 fieldNet.update(hero)를 부르고, 렌더(render/field/peers.ts)는 fieldNet.peers를 읽는다(FieldNetHook 모양).
// - 연결: 같은 출처 /api/field/ws?zone=&ch= (wss), 끊기면 지수 백오프(1s → 30s, 지터)로 다시 붙는다. 같은 채널로 돌아간다.
// - 송신: 10Hz, 바뀐 것이 있을 때만 pos. 서 있으면 25초마다 텍스트 ping(서버 자동 응답 — DO를 깨우지 않는다).
// - 수신: 스냅 → peers 고정 크기 배열(보간용 이전/다음 위치), 월드 보스 상태·결과.
// - 오프라인·서버 오류면 조용히 혼자 필드(예외를 던지지 않는다, 게임을 막지 않는다).
// update()·메시지 처리 안에서 새 객체를 만들지 않는다(인코더 버퍼·peers·보스·결과 묶음은 미리 잡는다).
import {
  BossPhase, FIELD_BOSS, FIELD_CHANNEL_CAPACITY, FIELD_KEEPALIVE_MS, FIELD_MIN_LEN, FIELD_POS_HEARTBEAT_MS, FIELD_PROTO_VERSION, FIELD_TICK_MS, FIELD_WS_PATH,
  FieldErr, FieldMsg, FieldReader, FieldWriter, makePeers, parseChannel, qCoord, qFacing, type FieldPeer,
} from '../shared/fieldNet';

/** 필드 장면이 매 틱 넘기는 영웅 상태(HeroAct 번호) */
export interface FieldHeroInput { x: number; z: number; facing: number; act: number }
/** 레인 F 필드 장면이 꽂는 자리와 같은 모양 */
export interface FieldNetHook {
  update(h: FieldHeroInput): void;
  readonly peers: readonly FieldPeer[];
}

/** 브라우저 WebSocket 중 쓰는 부분(테스트는 가짜를 넣는다) */
export interface FieldSocketLike {
  binaryType: string;
  readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: ArrayBufferView | string): void;
  close(code?: number, reason?: string): void;
}

export type FieldNetStatus = 'idle' | 'connecting' | 'online' | 'retrying' | 'stopped';

export interface FieldNetOptions {
  zone?: string;
  channel?: number;
  name: string;
  /** 0 카엘, 1 라이라 */
  hero?: number;
  cycle?: number;
  /** 하위 4비트 무기 단계, 상위 4비트 갑옷 단계 */
  look?: number;
  /** 접속 주소 직접 지정(QA 서버). 없으면 같은 출처 FIELD_WS_PATH */
  url?: string;
  /** 시계(ms, 단조). 기본 performance.now */
  now?: () => number;
  /** 벽시계(ms). 서버 시각 차이 계산용. 기본 Date.now */
  wallNow?: () => number;
  /** WebSocket 만들기(테스트) */
  socket?: (url: string) => FieldSocketLike;
  /** 타이머(테스트) */
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
  /** 백오프 상한(ms) */
  maxBackoffMs?: number;
  /** true면 Welcome 뒤 ackSpawn()이 불릴 때까지 pos를 보내지 않는다(연결 쪽이 영웅을 서버 시작 좌표로 옮기기 전에 옛 좌표가 나가 reject되는 것을 막는다) */
  holdPos?: boolean;
}

/** 월드 보스 보기(읽기 전용으로 쓴다) */
export interface FieldBossView {
  phase: number;
  no: number;
  hp: number;
  maxHp: number;
  x: number;
  z: number;
  /** 다음 등장·이번 종료 시각(로컬 벽시계 ms, 서버 시각 차이 보정) */
  nextAt: number;
  endsAt: number;
  /** 바뀔 때마다 오른다(UI 갱신 판단) */
  serial: number;
}
export interface FieldResultRow { rank: number; tier: number; ratio: number; dmg: number; name: string }
export interface FieldResultView {
  no: number;
  total: number;
  meRank: number;
  meTier: number;
  meRatio: number;
  meDmg: number;
  count: number;
  rows: FieldResultRow[];
  serial: number;
}

const OPEN = 1;
const RESULT_ROWS = 5;

export class FieldNet implements FieldNetHook {
  readonly peers: FieldPeer[] = makePeers(FIELD_CHANNEL_CAPACITY);
  status: FieldNetStatus = 'idle';
  /** 서버가 정한 내 id(0 = 아직) */
  you = 0;
  /** 지금 채널(1부터)과 채널 인원(나 포함) */
  channel = 1;
  count = 0;
  /** 명단에 있는 다른 사람 수 / 그중 관심 영역 안 */
  peerCount = 0;
  visibleCount = 0;
  /** 서버 벽시계 - 로컬 벽시계(ms) */
  clockOffset = 0;
  /** 마지막 오류 코드(FieldErr, 0 = 없음) */
  lastError = 0;
  /** 연결 시도 횟수(백오프 단계) */
  attempts = 0;
  /** 서버가 위치를 거부하고 돌려준 자리(serial이 오르면 필드 장면이 영웅을 되돌릴 수 있다) */
  readonly correction = { x: 0, z: 0, serial: 0 };
  readonly boss: FieldBossView = { phase: BossPhase.None, no: 0, hp: 0, maxHp: FIELD_BOSS.maxHp, x: FIELD_BOSS.x, z: FIELD_BOSS.z, nextAt: 0, endsAt: 0, serial: 0 };
  readonly result: FieldResultView = {
    no: 0, total: 0, meRank: 0, meTier: 0, meRatio: 0, meDmg: 0, count: 0, serial: 0,
    rows: Array.from({ length: RESULT_ROWS }, () => ({ rank: 0, tier: 0, ratio: 0, dmg: 0, name: '' })),
  };
  /** 스냅 수신 수(QA·UI) */
  snaps = 0;
  /** 이번 연결에서 보낸 pos 수(QA) */
  sentPos = 0;
  /**
   * Welcome이 알려 준 서버 시작 좌표(서버가 입구로 옮겼을 수 있다).
   * FieldNetLink가 serial이 오를 때마다 영웅을 이 자리로 옮기고 ackSpawn()을 부른다(P2c)
   */
  spawnX = 0;
  spawnZ = 0;
  /** Welcome을 받을 때마다 오른다 */
  spawnSerial = 0;
  /** holdPos 중 영웅 이동 확인을 기다리는가 */
  private spawnPending = false;

  private readonly o: Required<Omit<FieldNetOptions, 'url' | 'socket' | 'setTimer' | 'clearTimer'>> & Pick<FieldNetOptions, 'url' | 'socket' | 'setTimer' | 'clearTimer'>;
  private readonly w = new FieldWriter(64);
  private readonly r = new FieldReader();
  private ws: FieldSocketLike | null = null;
  private joined = false;
  private timer: unknown = null;
  private wanted = false;
  private seq = 0;
  private lastSend = -1e9;
  private lastPing = 0;
  private sentX = 0; private sentZ = 0; private sentF = 0; private sentA = -1;
  private hx = 0; private hz = 0; private hf = 0; private ha = 0; private hasHero = false;
  private pendingDmg = 0;
  private lastHitSend = -1e9;
  private serialNext = 1;
  private snapMark = 0;
  /** id → 칸 번호(명단이 바뀔 때만 고친다) */
  private readonly slotOf = new Map<number, number>();
  private readonly seen = new Uint32Array(FIELD_CHANNEL_CAPACITY);

  constructor(options: FieldNetOptions) {
    this.o = {
      zone: 'r1', channel: 1, hero: 0, cycle: 0, look: 0, maxBackoffMs: 30_000,
      now: () => performance.now(), wallNow: () => Date.now(),
      ...options,
    } as typeof this.o;
    this.channel = parseChannel(this.o.channel);
  }

  /** 연결 시작(필드 장면에 들어올 때). 몇 번 불러도 된다 */
  start(): void {
    if (this.wanted) return;
    this.wanted = true;
    this.connect();
  }
  /** 연결 끊고 멈춘다(필드에서 나갈 때) */
  stop(): void {
    this.wanted = false;
    this.cancelTimer();
    this.teardown(1000, 'leave');
    this.status = 'stopped';
  }
  /** 영웅을 Welcome 시작 좌표로 옮겼다 — 이제부터 pos를 보낸다 */
  ackSpawn(): void { this.spawnPending = false; }
  /** 이름·외형이 바뀌면(다음 join부터 반영) */
  setIdentity(name: string, hero: number, cycle: number, look: number): void {
    this.o.name = name; this.o.hero = hero; this.o.cycle = cycle; this.o.look = look;
  }
  get online(): boolean { return this.status === 'online' && this.joined; }

  /** 매 틱(60Hz): 영웅 상태를 받아 10Hz로 보낸다. 할당 없음 */
  update(h: FieldHeroInput): void {
    this.hx = h.x; this.hz = h.z; this.hf = h.facing; this.ha = h.act; this.hasHero = true;
    const ws = this.ws;
    if (!ws || !this.joined || ws.readyState !== OPEN) return;
    const t = this.o.now();
    if (t - this.lastSend >= FIELD_TICK_MS && !this.spawnPending) {
      const qx = qCoord(h.x), qz = qCoord(h.z), qf = qFacing(h.facing);
      // 값이 같아도 FIELD_POS_HEARTBEAT_MS마다 한 건은 보낸다(같은 좌표 — 속도 검사에 걸리지 않고 서버 Idle 시계를 갱신)
      if (qx !== this.sentX || qz !== this.sentZ || qf !== this.sentF || h.act !== this.sentA || t - this.lastSend >= FIELD_POS_HEARTBEAT_MS) {
        this.seq = (this.seq + 1) & 0xffff;
        this.send(this.w.pos(this.seq, h.x, h.z, h.facing, h.act));
        this.sentX = qx; this.sentZ = qz; this.sentF = qf; this.sentA = h.act;
        this.lastSend = t; this.lastPing = t;
        this.sentPos++;
      }
    }
    if (this.pendingDmg > 0 && t - this.lastHitSend >= FIELD_TICK_MS) {
      const dmg = Math.min(this.pendingDmg, FIELD_BOSS.hitMax);
      this.pendingDmg -= dmg;
      this.lastHitSend = t;
      this.send(this.w.hit(this.boss.no, dmg));
    }
    if (t - this.lastPing >= FIELD_KEEPALIVE_MS) {
      this.lastPing = t;
      try { ws.send('ping'); } catch { /* 닫히는 중 */ }
    }
  }

  /**
   * 월드 보스에 준 피해(게임 판정 결과). 10Hz로 묶어 보낸다(서버 빈도·상한 검사에 맞춘다). 보스가 없으면 버린다.
   * 한 번에 보낼 수 있는 양은 FIELD_BOSS.hitMax, 남은 것은 다음 틱으로 넘긴다(쌓인 양은 최대 3초 분량까지만).
   */
  hitBoss(dmg: number): void {
    if (!this.online || this.boss.phase !== BossPhase.Alive || !(dmg > 0)) return;
    this.pendingDmg = Math.min(this.pendingDmg + Math.floor(dmg), FIELD_BOSS.hitMax * 30);
  }

  // ── 연결 ───────────────────────────────────────────────

  private url(): string | null {
    const q = '?zone=' + encodeURIComponent(this.o.zone) + '&ch=' + this.channel;
    if (this.o.url) return this.o.url + (this.o.url.includes('?') ? '&' + q.slice(1) : q);
    const loc = (globalThis as { location?: { protocol: string; host: string } }).location;
    if (!loc || !loc.host) return null;
    return (loc.protocol === 'https:' ? 'wss://' : 'ws://') + loc.host + FIELD_WS_PATH + q;
  }

  private connect(): void {
    if (!this.wanted) return;
    this.cancelTimer();
    const url = this.url();
    const make = this.o.socket ?? ((u: string) => {
      const W = (globalThis as { WebSocket?: new (u: string) => FieldSocketLike }).WebSocket;
      if (!W) throw new Error('no-websocket');
      return new W(u);
    });
    if (!url) { this.status = 'stopped'; return; }
    this.status = 'connecting';
    this.attempts++;
    let ws: FieldSocketLike;
    try { ws = make(url); } catch { this.retry(); return; }
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      const x = this.hasHero ? this.hx : 0, z = this.hasHero ? this.hz : 0;
      this.send(this.w.join(this.o.hero, this.o.cycle, this.o.look, this.hf, x, z, this.o.name));
    };
    ws.onmessage = (ev) => { if (this.ws === ws) this.onMessage(ev.data); };
    ws.onerror = () => { /* onclose가 뒤따른다 */ };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.onDisconnected();
      // 다른 버전(4003)·내보냄(4005)·대체(4000: 같은 계정이 다른 창에서 들어왔다)·무입력(4006)은 다시 붙지 않는다
      if (ev.code === 4003 || ev.code === 4005 || ev.code === 4000 || ev.code === 4006) { this.wanted = false; this.status = 'stopped'; return; }
      this.retry();
    };
  }

  private retry(): void {
    if (!this.wanted) return;
    this.status = 'retrying';
    const n = Math.min(this.attempts, 6);
    const base = Math.min(this.o.maxBackoffMs, 1000 * 2 ** (n - 1));
    const delay = base * (0.75 + Math.random() * 0.5);
    const set = this.o.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    this.timer = set(() => { this.timer = null; this.connect(); }, delay);
  }
  private cancelTimer(): void {
    if (this.timer === null) return;
    (this.o.clearTimer ?? ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>)))(this.timer);
    this.timer = null;
  }
  private teardown(code: number, reason: string): void {
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try { ws.close(code, reason); } catch { /* 무시 */ }
    }
    this.onDisconnected();
  }
  /** 끊기면 다른 사람은 모두 지운다(혼자 필드). 보스 상태는 마지막 것을 남긴다 */
  private onDisconnected(): void {
    this.joined = false;
    this.you = 0;
    this.pendingDmg = 0;
    for (const p of this.peers) { p.active = false; p.visible = false; }
    this.slotOf.clear();
    this.peerCount = 0; this.visibleCount = 0;
  }
  private send(len: number): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN) return;
    try { ws.send(this.w.view(len)); } catch { /* 닫히는 중 */ }
  }

  // ── 수신 ───────────────────────────────────────────────

  private onMessage(data: unknown): void {
    if (!(data instanceof ArrayBuffer)) return; // 'pong' 등 텍스트
    const r = this.r.reset(data);
    if (r.version !== FIELD_PROTO_VERSION) { this.lastError = FieldErr.Version; return; }
    const min = FIELD_MIN_LEN[r.type];
    if (min === undefined || r.len < min) return;
    const t = this.o.now();
    switch (r.type) {
      case FieldMsg.Welcome: {
        this.you = r.u16(); this.channel = r.u8r(); this.count = r.u8r();
        this.spawnX = r.coord(); this.spawnZ = r.coord(); this.spawnSerial++;
        this.spawnPending = this.o.holdPos === true;
        this.clockOffset = r.f64() - this.o.wallNow();
        this.joined = true;
        this.status = 'online';
        this.attempts = 0;
        this.lastError = 0;
        this.sentA = -1; this.lastSend = -1e9; this.lastPing = t;
        break;
      }
      case FieldMsg.PeerAdd: {
        const id = r.u16(), hero = r.u8r(), cycle = r.u8r(), look = r.u8r(), count = r.u8r();
        const name = r.name();
        this.count = count;
        if (id === this.you || name === null) break;
        let slot = this.slotOf.get(id);
        if (slot === undefined) {
          slot = this.peers.findIndex((p) => !p.active);
          if (slot < 0) break;
          this.slotOf.set(id, slot);
        }
        const p = this.peers[slot];
        p.active = true; p.visible = false; p.id = id; p.name = name; p.hero = hero; p.cycle = cycle; p.look = look;
        p.serial = this.serialNext++;
        p.t0 = p.t1 = 0;
        this.recount();
        break;
      }
      case FieldMsg.PeerLeave: {
        const id = r.u16();
        this.count = r.u8r();
        const slot = this.slotOf.get(id);
        if (slot !== undefined) {
          this.slotOf.delete(id);
          const p = this.peers[slot];
          p.active = false; p.visible = false;
          this.recount();
        }
        break;
      }
      case FieldMsg.Snap: {
        r.u16();
        const n = r.u8r();
        const mark = ++this.snapMark;
        for (let i = 0; i < n && r.has(8); i++) {
          const id = r.u16(), x = r.coord(), z = r.coord(), f = r.facing(), act = r.u8r();
          const slot = this.slotOf.get(id);
          if (slot === undefined) continue;
          const p = this.peers[slot];
          this.seen[slot] = mark;
          if (!p.visible || p.t1 === 0 || t - p.t1 > 1500) {
            // 관심 영역에 새로 들어왔거나 오래 멈췄다: 그 자리에서 시작(보간으로 멀리서 미끄러져 오지 않게)
            p.t0 = t - FIELD_TICK_MS; p.x0 = x; p.z0 = z; p.f0 = f;
          } else {
            // 보간 중이던 현재 위치를 이전 점으로(스냅 지터에 튀지 않게)
            p.x0 = p.x1; p.z0 = p.z1; p.f0 = p.f1; p.t0 = p.t1;
          }
          p.t1 = t; p.x1 = x; p.z1 = z; p.f1 = f; p.act = act;
          p.visible = true;
        }
        for (let i = 0; i < this.peers.length; i++) {
          const p = this.peers[i];
          if (p.active && p.visible && this.seen[i] !== mark) p.visible = false;
        }
        this.snaps++;
        this.recount();
        break;
      }
      case FieldMsg.Boss: {
        const b = this.boss;
        b.no = r.u16(); b.phase = r.u8r(); b.hp = r.u32(); b.maxHp = r.u32(); b.x = r.coord(); b.z = r.coord();
        b.nextAt = r.f64() - this.clockOffset; b.endsAt = r.f64() - this.clockOffset;
        b.serial++;
        if (b.phase !== BossPhase.Alive) this.pendingDmg = 0;
        break;
      }
      case FieldMsg.BossResult: {
        const s = this.result;
        s.no = r.u16(); s.total = r.u32(); s.meRank = r.u8r(); s.meTier = r.u8r(); s.meRatio = r.u16() / 10000; s.meDmg = r.u32();
        const n = r.u8r();
        let k = 0;
        for (let i = 0; i < n && k < RESULT_ROWS && r.has(8); i++) {
          const row = s.rows[k];
          row.rank = r.u8r(); row.tier = r.u8r(); row.ratio = r.u16() / 10000; row.dmg = r.u32();
          const name = r.name();
          if (name === null) break;
          row.name = name;
          k++;
        }
        s.count = k;
        s.serial++;
        break;
      }
      case FieldMsg.Correct: {
        this.correction.x = r.coord(); this.correction.z = r.coord(); this.correction.serial++;
        // 서버가 아는 자리를 보낸 것으로 친다(다음 틱에 다시 보낸다)
        this.sentA = -1;
        break;
      }
      case FieldMsg.Error: {
        this.lastError = r.u8r();
        break;
      }
    }
  }

  private recount(): void {
    let a = 0, v = 0;
    for (const p of this.peers) { if (p.active) { a++; if (p.visible) v++; } }
    this.peerCount = a; this.visibleCount = v;
  }
}
